/**
 * isiZulu: Compose
 *
 * What this file covers: Writing a single message to one person or a few, outside a conversation.
 *
 * Tone: Friendly and direct. Make clear who will receive the message and on which channel.
 *
 * isiZulu: Everyday isiZulu as used in South African business. Keys not translated yet fall back to English, so
 * a missing key is never an error.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const compose: Catalog = {
  'compose.title': 'Umyalezo omusha',
  'compose.description': 'Thumela umyalezo kumuntu oyedwa, noma kwabambalwa. Bakhethe ngegama noma ngenombolo, futhi impendulo izofika ebhokisini lakho.',
  'compose.step.who': 'Uya kubani',
  'compose.step.what': 'Uthini',
  'compose.step.send': 'Izindleko nokuthumela',
  'compose.number.label': 'Thumela usuka ku',
  'compose.number.hint': 'Inombolo lo myalezo ophuma kuyo. Impendulo ifika ebhokisini laleyo nombolo.',
  'compose.recipients.label': 'Kuya ku',
  'compose.recipients.hint': 'Sesha igama noma inombolo, noma thayipha inombolo engakalondolozwa.',
  'compose.recipients.placeholder': 'Igama noma inombolo',
  'compose.recipients.remove': 'Susa {{who}}',
  'compose.recipients.notInBook': 'Akakho kothintana nabo okwamanje',
  'compose.recipients.noName': 'Alikho igama elilondoloziwe',
  'compose.recipients.noMatches': 'Akekho ofanayo nalokho. Thayipha inombolo ephelele ukuze uthumele komusha.',
  'compose.recipients.count': '{{count}} kwabangu-{{max}} abakhethiwe',
  'compose.recipients.clear': 'Susa bonke',
  'compose.recipients.full.title': 'Yilokho kuphela okwamukelekayo lapha',
  'compose.recipients.full.body': 'Umyalezo obhalwe lapha uya kubantu abangeqi ku-{{max}}. Uma bebaningi kunalokho, thumela umsakazo kuthegi noma kwisigaba.',
  'compose.window.badge.open': 'Angathola umyalezo',
  'compose.window.badge.needsTemplate': 'Udinga isifanekiso',
  'compose.window.badge.templateOnly': 'Isifanekiso kuphela',
  'compose.window.badge.optedOut': 'Uphumile',
  'compose.window.why.openHours': 'Ukubhalele, kusele amahora angu-{{hours}} okuphendula ngamazwi akho',
  'compose.window.why.openMinutes': 'Ukubhalele, kusele imizuzu engu-{{minutes}} yokuphendula ngamazwi akho',
  'compose.window.why.never': 'Akakaze akubhalele',
  'compose.window.why.expiredDays': 'Ugcine ukuzwa kuye ezinsukwini ezingu-{{days}} ezedlule',
  'compose.window.why.expiredHours': 'Ugcine ukuzwa kuye emahoreni angu-{{hours}} edlule',
  'compose.window.why.optedOut': 'Ucele ukungatholi imiyalezo',
  'compose.window.closed.title': 'Lona udinga isifanekiso esamukelwe',
  'compose.window.closed.body': 'I-WhatsApp ivumela umyalezo ngamazwi akho kuphela emahoreni angu-24 ngemva kokuba umuntu ekubhalele. Khetha isifanekiso esamukelwe esikhundleni salokho.',
  'compose.window.mixed.title': 'Laba bantu abakho endaweni efanayo',
  'compose.window.mixed.body': 'Abangu-{{closed}} kwabangu-{{total}} abakubhalelanga emahoreni angu-24 edlule, futhi umyalezo owodwa uya kubo bonke, ngakho kumele kube isifanekiso esamukelwe.',
  'compose.window.optedOut': 'Abangu-{{count}} kwabakhethiwe bacele ukungatholi imiyalezo. Bayashiywa futhi abakhokhelwa.',
  'compose.message.label': 'Umyalezo',
  'compose.message.hint': 'Ngamazwi akho. Thayipha u-{{ ukuze ungeze okuthile ngomuntu ngamunye, njengegama lakhe.',
  'compose.message.placeholder': 'Bhala umyalezo wakho',
  'compose.message.count': '{{length}} kwangu-{{max}}',
  'compose.message.previewEmpty': 'Umyalezo wakho uzobonakala lapha',
  'compose.message.previewBusiness': 'Ibhizinisi lakho',
  'compose.template.label': 'Isifanekiso',
  'compose.template.hint': 'Yizifanekiso ezamukelwe yi-WhatsApp kuphela ezingathunyelwa ngaphandle kwewindi lamahora angu-24.',
  'compose.template.placeholders': 'Gcwalisa izikhala',
  'compose.template.placeholders.hint': 'Thayipha u-{{ ukuze ugcwalise isikhala ekhadini loxhumana naye lomuntu ngamunye, njengegama lakhe. Umuntu ngamunye uthola inani lakhe.',
  'compose.template.placeholder': 'Isikhala {{n}}',
  'compose.template.noPlaceholders': 'Lesi sifanekiso asinazo izikhala okumele zigcwaliswe.',
  'compose.template.noneApproved.title': 'Azikho izifanekiso ezamukelwe okwamanje',
  'compose.template.noneApproved.body': 'I-WhatsApp kumele yamukele isifanekiso ngaphambi kokuba sithunyelwe kumuntu ongakubhalelanga muva nje.',
  'compose.template.noneApproved.action': 'Dala isifanekiso',
  'compose.template.devFallbackHidden': 'Izifanekiso ezingu-{{count}} zifihliwe ngoba i-WhatsApp ayikazamukeli.',
  'compose.summary.recipients': 'Abantu',
  'compose.summary.excluded': 'Abangu-{{count}} bashiyiwe',
  'compose.summary.cost': 'Lokhu kuzobiza',
  'compose.summary.balance': 'Ibhalansi',
  'compose.summary.balanceAfter': '{{after}} okusele ngemva kokuthumela',
  'compose.summary.balanceUnknown': 'Khetha ukuthi kuya kubani, sizokubalela.',
  'compose.summary.short.title': 'Azanele izikweletu',
  'compose.summary.short.body': 'Udinga {{shortfall}} ngaphezulu ukuze uthumele lokhu.',
  'compose.summary.short.action': 'Faka imali',
  'compose.summary.broadcastHint': 'Uthumela kubantu abaningi?',
  'compose.summary.broadcastAction': 'Thumela umsakazo',
  'compose.action.send': 'Thumela kwabangu-{{count}}',
  'compose.action.sendTemplate': 'Thumela isifanekiso kwabangu-{{count}}',
  'compose.error.bodyRequired': 'Bhala umyalezo ngaphambi kokuwuthumela.',
  'compose.error.templateRequired': 'Khetha isifanekiso esamukelwe.',
  'compose.error.placeholderRequired': 'Gcwalisa lokhu ngaphambi kokuthumela.',
  'compose.error.numbersFailed': 'Izinombolo zakho ze-WhatsApp azikwazanga ukulayishwa okwamanje.',
  'compose.error.noNumbers.title': 'Ayikho inombolo ye-WhatsApp exhunyiwe',
  'compose.error.noNumbers.body': 'Umyalezo kumele uphume enombolweni exhunyiwe. Xhuma eyodwa, bese ungathumela kunoma ubani.',
  'compose.error.noNumbers.action': 'Xhuma inombolo',
  'compose.refusal.windowClosed': 'Abanye balaba bantu abakubhalelanga emahoreni angu-24 edlule, ngakho kumele kube isifanekiso esamukelwe. Akukho okuthunyelwe.',
  'compose.refusal.TOO_MANY_RECIPIENTS': 'Yibantu abaningi kunalokho okwamukelwa umyalezo obhalwe lapha. Thumela umsakazo kuthegi noma kwisigaba esikhundleni salokho.',
  'compose.refusal.NUMBER_REQUIRED': 'Sho ukuthi lo myalezo uphuma kuyiphi inombolo yakho.',
  'compose.refusal.NO_ACTIVE_NUMBER': 'Ayikho inombolo ye-WhatsApp exhunyiwe, ngakho akukho okungathunyelwa.',
  'compose.refusal.VALIDATION': 'Kukhona okungakagcwaliswa kulo myalezo.',
  'compose.refusal.unknown': 'Lokhu akukwazanga ukuthunyelwa okwamanje. Zama futhi emzuzwini.',
  'compose.toast.sent': 'Kuthunyelwe kwabangu-{{count}}.',
  'compose.toast.partial': 'Kuthunyelwe kwabangu-{{sent}}, kodwa abangu-{{failed}} abafinyelelwanga.',
  'compose.toast.noneSent': 'Akukho okuthunyelwe. Buka ngezansi ukuthi kwenzekeni.',
  'compose.results.allSent.title': 'Konke kuthunyelwe',
  'compose.results.allSent.body': 'Lokhu kufinyelele bonke abangu-{{count}}. Izimpendulo zabo zizofika ebhokisini lakho.',
  'compose.results.noneSent.title': 'Akukho okuthunyelwe',
  'compose.results.noneSent.body': 'Akekho kwabangu-{{count}} ofinyelelwe. Okungahambanga kahle kuboniswa eceleni komuntu ngamunye.',
  'compose.results.partial.title': 'Eminye yale miyalezo ayifikanga',
  'compose.results.partial.body': 'Abangu-{{sent}} bafinyelelwe futhi abangu-{{failed}} abafinyelelwanga. Ungazama futhi labo abangaphumelelanga ngaphandle kokuthumela kabili komunye.',
  'compose.results.delivered': 'Isendleleni',
  'compose.results.open': 'Vula ingxoxo',
  'compose.results.badge.sent': 'Kuthunyelwe',
  'compose.results.badge.failed': 'Akuthunyelwanga',
  'compose.results.backToInbox': 'Buyela ebhokisini',
  'compose.results.another': 'Bhala omunye',
  'compose.results.retry': 'Zama abangu-{{count}} futhi',
  'compose.results.reason.unknown': 'Lona awufikanga.',
  'compose.results.reason.OPTED_OUT': 'Bacele ukungatholi imiyalezo.',
  'compose.results.reason.WINDOW_CLOSED': 'Abakubhalelanga emahoreni angu-24 edlule, ngakho lokhu bekudinga isifanekiso esamukelwe.',
  'compose.results.reason.INSUFFICIENT_CREDITS': 'Bezinganele izikweletu zokuthumela lona.',
  'compose.results.reason.PHONE_NOT_FOUND': 'Inombolo lokhu okuthunyelwe kuyo ayisaxhunyiwe.',
  'compose.results.reason.CHANNEL_UNREACHABLE': 'I-WhatsApp ayifinyelelekanga. Akukho okuthunyelwe kubo.',
  'compose.results.reason.SEND_FAILED': 'I-WhatsApp ayilwamukelanga lolu.',
  'compose.results.reason.META_REFUSED': 'I-WhatsApp ayilwamukelanga lolu.',
  'compose.results.reason.SEND_UNREADABLE': 'Lokhu kuphumile kodwa asikwazanga ukuqinisekisa okwenzekile.',
  'compose.number.unnamed': 'Inombolo esasethwa',
  'compose.results.reason.ACCOUNT_NOT_PAYABLE': 'I-WhatsApp iyenqabile ngenxa yenkinga yokukhokha ngakithi, hhayi ngakuwe. Siyayilungisa, futhi awudingi ukwenza lutho ku-Meta.',
  'compose.results.reason.UNDELIVERABLE': 'I-WhatsApp ayikwazanga ukuyiletha kuleyo nombolo.',
  'compose.results.reason.NUMBER_NOT_REGISTERED': 'Inombolo othumele ngayo ayibhalisiwe ku-WhatsApp.',
  'compose.results.reason.MEDIA_NOT_FETCHED': 'I-WhatsApp ayikwazanga ukulanda ifayela elinamathiselwe.'
};

export default compose;
